import { safeCss } from '@frontend/web-utils';
import type { IconColorVariant } from '../Icon/Icon';
import type { ButtonState, ButtonVariant } from './Button.types';

export type VariantStyle = {
  background: string;
  border: string;
  hover: string;
  text: string;
};

const variantStyles: Record<ButtonVariant, Record<ButtonState, VariantStyle>> = {
  destructive: {
    default: {
      background: safeCss('bg-red-100'),
      border: safeCss('border-red-100'),
      hover: safeCss('hover:bg-red-200'),
      text: safeCss('text-red-600'),
    },
    disabled: {
      background: safeCss('bg-neutral-200'),
      border: safeCss('border-neutral-200'),
      hover: safeCss('cursor-not-allowed'),
      text: safeCss('text-neutral-500'),
    },
    pressed: {
      background: safeCss('bg-red-200'),
      border: safeCss('border-red-200'),
      hover: '',
      text: safeCss('text-red-700'),
    },
  },
  gradient: {
    default: {
      background: safeCss(
        '[background:linear-gradient(#292929,#292929)_padding-box,linear-gradient(135deg,#A2DBC6_21.18%,#E5EDF7_52.8%,#EEFB86_89.37%)_border-box]',
      ),
      border: safeCss('border-transparent'),
      hover: safeCss('hover:opacity-90'),
      text: safeCss('text-white'),
    },
    disabled: {
      background: safeCss('bg-neutral-200'),
      border: safeCss('border-neutral-200'),
      hover: safeCss('cursor-not-allowed'),
      text: safeCss('text-neutral-500'),
    },
    pressed: {
      background: safeCss(
        '[background:linear-gradient(#3d3d3d,#3d3d3d)_padding-box,linear-gradient(135deg,#A2DBC6_21.18%,#E5EDF7_52.8%,#EEFB86_89.37%)_border-box]',
      ),
      border: safeCss('border-transparent'),
      hover: '',
      text: safeCss('text-white'),
    },
  },
  primary: {
    default: {
      background: safeCss('bg-neutral-950'),
      border: safeCss('border-neutral-950'),
      hover: safeCss('hover:bg-neutral-900'),
      text: safeCss('text-white'),
    },
    disabled: {
      background: safeCss('bg-neutral-200'),
      border: safeCss('border-neutral-200'),
      hover: safeCss('cursor-not-allowed'),
      text: safeCss('text-neutral-500'),
    },
    pressed: {
      background: safeCss('bg-neutral-900'),
      border: safeCss('border-neutral-900'),
      hover: '',
      text: safeCss('text-white'),
    },
  },
  secondary: {
    default: {
      background: safeCss('bg-transparent'),
      border: safeCss('border-neutral-950'),
      hover: safeCss('hover:bg-cream-100'),
      text: safeCss('text-neutral-950'),
    },
    disabled: {
      background: safeCss('bg-transparent'),
      border: safeCss('border-neutral-300'),
      hover: safeCss('cursor-not-allowed'),
      text: safeCss('text-neutral-500'),
    },
    pressed: {
      background: safeCss('bg-cream-100'),
      border: safeCss('border-neutral-950'),
      hover: '',
      text: safeCss('text-neutral-950'),
    },
  },
  secondarySupport: {
    default: {
      background: safeCss('bg-cream-400'),
      border: safeCss('border-cream-400'),
      hover: safeCss('hover:bg-cream-500'),
      text: safeCss('text-neutral-950'),
    },
    disabled: {
      background: safeCss('bg-neutral-200'),
      border: safeCss('border-neutral-200'),
      hover: safeCss('cursor-not-allowed'),
      text: safeCss('text-neutral-500'),
    },
    pressed: {
      background: safeCss('bg-cream-500'),
      border: safeCss('border-cream-500'),
      hover: '',
      text: safeCss('text-neutral-950'),
    },
  },
  success: {
    default: {
      background: safeCss('bg-green-600'),
      border: safeCss('border-green-600'),
      hover: safeCss('hover:bg-green-700'),
      text: safeCss('text-white'),
    },
    disabled: {
      background: safeCss('bg-neutral-200'),
      border: safeCss('border-neutral-200'),
      hover: safeCss('cursor-not-allowed'),
      text: safeCss('text-neutral-500'),
    },
    pressed: {
      background: safeCss('bg-green-700'),
      border: safeCss('border-green-700'),
      hover: '',
      text: safeCss('text-white'),
    },
  },
  tertiary: {
    default: {
      background: safeCss('bg-transparent'),
      border: safeCss('border-transparent'),
      hover: safeCss('hover:text-neutral-700'),
      text: safeCss('text-neutral-950'),
    },
    disabled: {
      background: safeCss('bg-transparent'),
      border: safeCss('border-transparent'),
      hover: safeCss('cursor-not-allowed'),
      text: safeCss('text-neutral-500'),
    },
    pressed: {
      background: safeCss('bg-transparent'),
      border: safeCss('border-transparent'),
      hover: '',
      text: safeCss('text-neutral-700'),
    },
  },
};

export const getVariantStyles = (variant: ButtonVariant, state: ButtonState): VariantStyle => {
  return variantStyles[variant][state];
};

export const getVariantClasses = (variant: ButtonVariant, disabled: boolean): string => {
  const state: ButtonState = disabled ? 'disabled' : 'default';
  const styles = getVariantStyles(variant, state);
  return [styles.background, styles.border, styles.text, styles.hover].filter(Boolean).join(' ');
};

export const getLoaderColor = (variant: ButtonVariant): string => {
  switch (variant) {
    case 'primary':
    case 'gradient':
    case 'success':
      return 'white';
    case 'secondary':
    case 'secondarySupport':
    case 'tertiary':
      return 'neutral.9';
    case 'destructive':
      return 'red.6';
    default:
      return 'neutral.9';
  }
};

export const getIconColor = (variant: ButtonVariant, disabled: boolean): IconColorVariant => {
  if (disabled) return 'secondary';

  switch (variant) {
    case 'primary':
    case 'gradient':
    case 'success':
      return 'white';
    case 'secondary':
    case 'secondarySupport':
    case 'tertiary':
      return 'primary';
    case 'destructive':
      return 'danger';
    default:
      return 'primary';
  }
};
